// Cómo se llaman en pantalla las cifras que este módulo sabe medir.
//
// Aparte del resto de los textos por un motivo que no es de tamaño: estas claves
// son un CONTRATO con el catálogo de analítica (`backend/analytics.js`), no copia
// de una pantalla. Cada `labelKey` que aquel fichero declara tiene aquí su
// traducción en los cuatro idiomas, y una que falte no rompe nada — sale el id
// hecho legible, en inglés, en medio de una pantalla en castellano.
//
// La ayuda de un dataset (`help`) es lo que alguien lee en el desplegable de
// Explorar antes de elegirlo. Dice qué es UNA FILA, que es la pregunta que de
// verdad distingue dos conjuntos de datos parecidos.

const analytics = {
  es: {
    purchases: {
      label: 'Compras',
      help: 'Una fila por documento de compra: petición de precio o pedido. Las anuladas quedan fuera',
    },
    dates: {
      issue: 'Fecha del pedido',
      expected: 'Fecha prevista',
      received: 'Fecha de recepción',
      created: 'Fecha de creación',
    },
    metrics: {
      spend: 'Comprado',
      spendWithTax: 'Comprado con IVA',
      purchases: 'Documentos',
      orders: 'Pedidos',
      committed: 'Comprometido',
      overdue: 'Pedidos fuera de plazo',
      daysLate: 'Días de retraso',
      leadTime: 'Plazo de entrega',
      average: 'Pedido medio',
      onTimeRate: 'Cumplimiento',
      received: 'Recibidos completos',
    },
    dimensions: {
      supplier: 'Proveedor',
      status: 'Estado',
      kind: 'Tipo de documento',
      project: 'Obra',
      customer: 'Cliente',
      currency: 'Moneda',
      author: 'Quién lo pidió',
    },
    filters: {
      total: 'Total del documento',
    },
  },
  ca: {
    purchases: {
      label: 'Compres',
      help: 'Una fila per document de compra: petició de preu o comanda. Les anul·lades queden fora',
    },
    dates: {
      issue: 'Data de la comanda',
      expected: 'Data prevista',
      received: 'Data de recepció',
      created: 'Data de creació',
    },
    metrics: {
      spend: 'Comprat',
      spendWithTax: 'Comprat amb IVA',
      purchases: 'Documents',
      orders: 'Comandes',
      committed: 'Compromès',
      overdue: 'Comandes fora de termini',
      daysLate: 'Dies de retard',
      leadTime: 'Termini de lliurament',
      average: 'Comanda mitjana',
      onTimeRate: 'Compliment',
      received: 'Rebuts complets',
    },
    dimensions: {
      supplier: 'Proveïdor',
      status: 'Estat',
      kind: 'Tipus de document',
      project: 'Obra',
      customer: 'Client',
      currency: 'Moneda',
      author: 'Qui ho va demanar',
    },
    filters: {
      total: 'Total del document',
    },
  },
  en: {
    purchases: {
      label: 'Purchases',
      help: 'One row per purchase document: a price request or an order. Cancelled ones are excluded',
    },
    dates: {
      issue: 'Order date',
      expected: 'Expected date',
      received: 'Received',
      created: 'Created',
    },
    metrics: {
      spend: 'Spend',
      spendWithTax: 'Spend with VAT',
      purchases: 'Documents',
      orders: 'Orders',
      committed: 'Committed',
      overdue: 'Overdue orders',
      daysLate: 'Days late',
      leadTime: 'Lead time',
      average: 'Average order',
      onTimeRate: 'Fulfilment',
      received: 'Received in full',
    },
    dimensions: {
      supplier: 'Supplier',
      status: 'Status',
      kind: 'Document kind',
      project: 'Job',
      customer: 'Customer',
      currency: 'Currency',
      author: 'Raised by',
    },
    filters: {
      total: 'Document total',
    },
  },
  fr: {
    purchases: {
      label: 'Achats',
      help: 'Une ligne par document d\'achat : demande de prix ou commande. Les annulés sont exclus',
    },
    dates: {
      issue: 'Date de commande',
      expected: 'Date prévue',
      received: 'Date de réception',
      created: 'Date de création',
    },
    metrics: {
      spend: 'Achats',
      spendWithTax: 'Achats TTC',
      purchases: 'Documents',
      orders: 'Commandes',
      committed: 'Engagé',
      overdue: 'Commandes en retard',
      daysLate: 'Jours de retard',
      leadTime: 'Délai de livraison',
      average: 'Commande moyenne',
      onTimeRate: 'Taux de service',
      received: 'Reçus complets',
    },
    dimensions: {
      supplier: 'Fournisseur',
      status: 'Statut',
      kind: 'Type de document',
      project: 'Chantier',
      customer: 'Client',
      currency: 'Devise',
      author: 'Demandé par',
    },
    filters: {
      total: 'Total du document',
    },
  },
}

/**
 * Adds the analytics vocabulary to the module's own catalogue, locale by locale.
 *
 * Merged here rather than pasted into the big literal so the block stays
 * readable and so adding a metric is one line in two files instead of four
 * edits in the middle of eight hundred.
 */
export function withAnalytics(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], analytics: analytics[locale] },
    }
  }
  return merged
}

export default analytics
